/**
 * PaymentReturnScreen — landing tras el checkout MP.
 *
 * MP redirige aquí con query params: ?status=success|failure|pending&tier=X&mode=auto|manual
 *
 * - success: muestra confetti + resumen del plan + CTA "Ir a mi cuenta"
 * - failure: explicación + CTA "Reintentar" o "Contactar soporte"
 * - pending: "Tu pago está procesándose, te avisamos por email"
 *
 * El estado real del user se actualiza vía webhook MP en backend, no acá.
 */
import { useEffect, useState } from "react";
import { Icon } from "../lib/Icon";
import { useAuth } from "../contexts/AuthContext";
import { getMySubscription, type SubscriptionDisplay } from "../api/subscriptions";

export function PaymentReturnScreen() {
  const { user, refreshUser } = useAuth();

  // Path-based params (más robusto que query: MP agrega `?` adicional que rompe parsing).
  // URL: /payments/return/{tier}/{mode}?collection_status=approved&...
  const pathParts = window.location.pathname.replace(/^\/payments\/return\/?/, "").split("/").filter(Boolean);
  const tier = pathParts[0] || "";
  const mode = pathParts[1] || "auto";

  // Status: lo deducimos primero del query de MP (collection_status / status), si no
  // viene asumimos "success" porque MP solo redirige al back_url tras success/failure/pending.
  // Soportamos tanto `?` único como múltiples `?` (tomando el último que parsea bien).
  const params = new URLSearchParams(
    window.location.search.replace(/\?(?=.*\?)/g, "&"),
  );
  const collectionStatus = params.get("collection_status") || params.get("status") || "";
  const status: "success" | "failure" | "pending" | "unknown" =
    collectionStatus === "approved"
      ? "success"
      : collectionStatus === "rejected" || collectionStatus === "cancelled"
      ? "failure"
      : collectionStatus === "pending" || collectionStatus === "in_process"
      ? "pending"
      : "success"; // MP redirige aquí solo si llegó a la pasarela; default optimista

  const [sub, setSub] = useState<SubscriptionDisplay | null>(null);
  const [loading, setLoading] = useState(true);

  // Polling: el webhook puede tardar unos segundos en activar el plan.
  // Refrescamos sub cada 3s hasta 30s o hasta que el status sea "active".
  // Cuando se active, auto-redirigimos a /config con full-reload (re-hidrata AuthContext).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;

    const tick = async () => {
      attempts++;
      try {
        const s = await getMySubscription();
        if (cancelled) return;
        setSub(s);
        if (s.status === "active") {
          await refreshUser();
          setLoading(false);
          // Auto-redirect después de 2.5s para que el user vea el "¡Pago aprobado!"
          // Full reload garantiza re-hidratación limpia del AuthContext + ConfigScreen.
          setTimeout(() => {
            if (!cancelled) {
              window.location.replace("/#/config");
              window.location.reload();
            }
          }, 2500);
          return;
        }
      } catch {
        // ignorar
      }
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(tick, 3000);
      } else {
        setLoading(false);
      }
    };

    tick();
    return () => { cancelled = true; };
  }, [user, refreshUser]);

  return (
    <div style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: 20,
      background: "var(--bg-subtle, #fafafa)",
    }}>
      <div className="card" style={{
        maxWidth: 540,
        width: "100%",
        padding: "40px 32px",
        textAlign: "center",
      }}>
        {status === "success" && (
          <SuccessView
            tier={tier}
            mode={mode}
            sub={sub}
            loading={loading}
          />
        )}
        {status === "failure" && (
          <FailureView tier={tier} mode={mode} />
        )}
        {status === "pending" && (
          <PendingView tier={tier} />
        )}
        {status !== "success" && status !== "failure" && status !== "pending" && (
          <div>
            <h2 style={{ marginTop: 0 }}>Estado desconocido</h2>
            <p className="muted">No pudimos determinar el estado de tu pago.</p>
            <a href="/" className="btn btn-primary" style={{ marginTop: 16 }}>
              Volver al inicio
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessView({ tier, mode, sub, loading }: { tier: string; mode: string; sub: SubscriptionDisplay | null; loading: boolean }) {
  const isActive = sub?.status === "active";
  return (
    <>
      <div style={{
        width: 72,
        height: 72,
        margin: "0 auto 16px",
        background: "linear-gradient(135deg, #10b981, #059669)",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        color: "#fff",
      }}>
        <Icon name="check" size={36} />
      </div>
      <h1 style={{ margin: "0 0 8px", fontFamily: "var(--font-display)", fontSize: 28 }}>
        ¡Pago aprobado!
      </h1>
      <p className="muted" style={{ marginBottom: 24, lineHeight: 1.5 }}>
        {isActive ? (
          <>Tu plan <b>{sub?.tier_label || tier}</b> ya está activo. Disfrutá de todas las funcionalidades premium.</>
        ) : loading ? (
          <>Estamos confirmando tu pago con MercadoPago. Esto puede tardar unos segundos…</>
        ) : (
          <>Tu pago fue procesado por MercadoPago. La activación se completará en unos minutos vía webhook. Te avisamos por email cuando esté listo.</>
        )}
      </p>
      {mode === "auto" && isActive && sub?.renews_at && (
        <div className="tiny muted" style={{ marginBottom: 16 }}>
          Próximo cobro automático: {new Date(sub.renews_at).toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        {/* Forzamos full-page reload con location.replace para que AuthContext re-hidrate
            con los datos frescos del user (plan_tier, subscription_status). Si usáramos
            <a href="/#/config"> el SPA sigue con el AuthContext viejo y muestra "no
            pudimos cargar tu plan" hasta refresh manual. */}
        <button
          className="btn btn-primary"
          onClick={() => { window.location.replace("/#/config"); window.location.reload(); }}
        >
          Ir a mi cuenta
        </button>
      </div>
    </>
  );
}

function FailureView({ tier, mode: _mode }: { tier: string; mode: string }) {
  return (
    <>
      <div style={{
        width: 72,
        height: 72,
        margin: "0 auto 16px",
        background: "linear-gradient(135deg, #ef4444, #dc2626)",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        color: "#fff",
      }}>
        <Icon name="x" size={36} />
      </div>
      <h1 style={{ margin: "0 0 8px", fontFamily: "var(--font-display)", fontSize: 28 }}>
        El pago no se completó
      </h1>
      <p className="muted" style={{ marginBottom: 24, lineHeight: 1.5 }}>
        MercadoPago no aprobó la transacción. Esto puede ser por fondos insuficientes,
        tarjeta vencida, o un rechazo del banco. Tu plan no fue modificado.
      </p>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          className="btn btn-primary"
          onClick={() => { window.location.replace("/#/config"); window.location.reload(); }}
        >
          Reintentar
        </button>
        <a
          href={`mailto:nilton.alfonso@nilabs.co?subject=Problema%20con%20pago%20AetherLite%20${tier}`}
          className="btn btn-ghost"
        >
          Contactar soporte
        </a>
      </div>
    </>
  );
}

function PendingView({ tier: _tier }: { tier: string }) {
  return (
    <>
      <div style={{
        width: 72,
        height: 72,
        margin: "0 auto 16px",
        background: "linear-gradient(135deg, #f59e0b, #d97706)",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        color: "#fff",
      }}>
        <Icon name="sparkle" size={36} />
      </div>
      <h1 style={{ margin: "0 0 8px", fontFamily: "var(--font-display)", fontSize: 28 }}>
        Tu pago está procesándose
      </h1>
      <p className="muted" style={{ marginBottom: 24, lineHeight: 1.5 }}>
        MercadoPago está validando la transacción (común con pagos en efectivo o
        transferencias). Te avisaremos por email cuando se confirme.
      </p>
      <button
        className="btn btn-primary"
        onClick={() => { window.location.replace("/#/config"); window.location.reload(); }}
      >
        Volver a mi cuenta
      </button>
    </>
  );
}
